import { ReactNode, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../authProvider";

interface ForcePasswordGuardProps {
  children: ReactNode;
}

/**
 * Guard component that checks if user needs to change password.
 * If user needs forced password change, redirects to /force-change-password.
 * Currently checks for COPYWRITER_CLIENT with email_confirmed = false.
 * Extensible for future conditions (password expiry, security policies, etc.).
 */
export const ForcePasswordGuard = ({ children }: ForcePasswordGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const needsPasswordChange = useCallback(() => {
    if (!user) return false;

    if (user.isClientRelated && user.email_confirmed === false) {
      return true;
    }

    return false;
  }, [user]);

  useEffect(() => {
    // Don't redirect if already on the force-change-password page
    if (location.pathname === "/force-change-password") {
      return;
    }

    if (needsPasswordChange()) {
      // Redirect to force password change page with return URL
      navigate(`/force-change-password?returnUrl=${location.pathname}`);
    }
  }, [user, location.pathname, navigate, needsPasswordChange]);

  // If user needs password change, don't render children (will redirect)
  if (needsPasswordChange() && location.pathname !== "/force-change-password") {
    return null;
  }

  return <>{children}</>;
};
